<script setup lang="ts">
import { computed, ref } from "vue";
import Icon from "./Icon.vue";
import { api, type OptSpec, type Server } from "../api";
import DirPicker from "./DirPicker.vue";

// Kreator naprawy: trzy kroki zamiast formularza z czternastoma opcjami.
// Klient podejmuje trzy decyzje — który serwer, która strona, czy tylko
// sprawdzić czy też naprawić. Reszta ma dobre domyślne i siedzi w „ustawieniach
// zaawansowanych", których większość nigdy nie otworzy.

const props = defineProps<{ servers: Server[]; opts: OptSpec[]; initialServerId?: string | null; hasBilling?: boolean }>();
const emit = defineEmits<{ started: [{ jobId: string; runId: string }]; close: [] }>();

const step = ref(1);
const serverId = ref(props.initialServerId ?? props.servers[0]?.id ?? "");
const target = ref("");
const siteUrl = ref("");
const mode = ref<"report" | "quarantine">("quarantine");
const advanced = ref<Record<string, string | boolean>>({});
const err = ref("");
const busy = ref(false);
const picking = ref(false);
const roots = ref<string[] | null>(null);
const rootsMsg = ref("");
const findingRoots = ref(false);

const PRIMARY = new Set(["target", "mode", "siteUrl"]);
const advancedOpts = computed(() => props.opts.filter((o) => !PRIMARY.has(o.key)));
for (const o of advancedOpts.value) {
  if (o.kind === "boolean") advanced.value[o.key] = false;
  else if (o.kind === "negate") advanced.value[o.key] = true;
  else if (o.default != null) advanced.value[o.key] = String(o.default);
  else advanced.value[o.key] = "";
}

const server = computed(() => props.servers.find((s) => s.id === serverId.value) ?? null);

// Adres WWW podpowiadamy z nazwy katalogu: /home/x/public_html/sklep.pl → https://sklep.pl
function guessSiteUrl(path: string): string {
  const last = path.replace(/\/+$/, "").split("/").pop() ?? "";
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(last) ? `https://${last}` : "";
}
function pickTarget(p: string) {
  target.value = p;
  if (!siteUrl.value) siteUrl.value = guessSiteUrl(p);
}

async function findRoots() {
  if (!serverId.value) return;
  findingRoots.value = true;
  rootsMsg.value = "Przeszukuję serwer…";
  roots.value = null;
  try {
    const r = await api.get<{ roots: string[] }>(`/api/servers/${encodeURIComponent(serverId.value)}/webroots`);
    roots.value = r.roots;
    rootsMsg.value = r.roots.length ? "Znalezione strony:" : "Nie znalazłem katalogów wyglądających na stronę. Użyj „Przeglądaj”.";
    if (r.roots.length === 1 && !target.value) pickTarget(r.roots[0]);
  } catch (e) {
    rootsMsg.value = "✗ " + (e as Error).message;
  } finally {
    findingRoots.value = false;
  }
}

function next() {
  err.value = "";
  if (step.value === 1 && !serverId.value) return (err.value = "Wybierz serwer.");
  if (step.value === 2 && !target.value.trim()) return (err.value = "Wskaż katalog strony.");
  if (step.value === 2 && siteUrl.value && !/^https?:\/\//i.test(siteUrl.value)) {
    return (err.value = "Adres strony musi zaczynać się od http:// lub https://");
  }
  step.value += 1;
}

async function start() {
  err.value = "";
  busy.value = true;
  try {
    const values: Record<string, string | boolean> = { target: target.value.trim(), mode: mode.value };
    if (siteUrl.value.trim()) values.siteUrl = siteUrl.value.trim();
    for (const o of advancedOpts.value) {
      const v = advanced.value[o.key];
      if (v === "" || v === undefined) continue;
      values[o.key] = v;
    }
    const d = await api.post<{ jobId: string; runId: string }>("/api/scan", { serverId: serverId.value, values });
    emit("started", d);
  } catch (e) {
    err.value = (e as Error).message;
  } finally {
    busy.value = false;
  }
}

function onKey(e: KeyboardEvent) {
  if (e.key === "Escape" && !picking.value) emit("close");
}
</script>

<template>
  <div class="backdrop" tabindex="-1" @click.self="emit('close')" @keydown="onKey">
    <div class="box" role="dialog" aria-label="Nowa naprawa">
      <div class="head">
        <div>
          <h2>Nowa naprawa</h2>
          <p class="muted">Trzy kroki. Nic nie zmienimy na serwerze bez Twojej decyzji w kroku 3.</p>
        </div>
        <button type="button" class="link" aria-label="Zamknij" @click="emit('close')"><Icon name="x" :size="18" /></button>
      </div>

      <ol class="steps">
        <li :class="{ on: step === 1, done: step > 1 }"><span>1</span> Serwer</li>
        <li :class="{ on: step === 2, done: step > 2 }"><span>2</span> Strona</li>
        <li :class="{ on: step === 3 }"><span>3</span> Zakres</li>
      </ol>

      <div class="content">
        <!-- Krok 1: serwer -->
        <template v-if="step === 1">
          <p v-if="!servers.length" class="empty">Nie masz jeszcze żadnego serwera. Dodaj go w zakładce <b>Serwery</b>.</p>
          <div class="cards">
            <button
              v-for="s in servers" :key="s.id" type="button" class="pick"
              :class="{ on: s.id === serverId }" @click="serverId = s.id"
            >
              <span class="ic"><Icon name="server" :size="24" /></span>
              <span class="grow">
                <span class="t">{{ s.label }}</span>
                <span class="d mono">{{ s.username }}@{{ s.host }}:{{ s.port }}</span>
              </span>
              <span v-if="s.id === serverId" class="chk"><Icon name="check" :size="18" /></span>
            </button>
          </div>
        </template>

        <!-- Krok 2: strona -->
        <template v-if="step === 2">
          <div class="field">
            <label for="wz-target">Katalog strony na serwerze</label>
            <div class="row">
              <input id="wz-target" v-model="target" type="text" class="mono grow" placeholder="/home/konto/public_html" @change="target && !siteUrl && (siteUrl = guessSiteUrl(target))">
              <button type="button" class="sec" @click="picking = true"><Icon name="folder" /> Przeglądaj</button>
            </div>
            <div class="row" style="margin-top:8px">
              <button type="button" class="sec small" :disabled="findingRoots" @click="findRoots">
                <Icon name="search" /> {{ findingRoots ? "szukam…" : "Znajdź strony automatycznie" }}
              </button>
              <span v-if="rootsMsg" class="hint">{{ rootsMsg }}</span>
            </div>
            <div v-if="roots?.length" class="chips">
              <button v-for="p in roots" :key="p" type="button" class="chip" :class="{ on: p === target }" @click="pickTarget(p)">{{ p }}</button>
            </div>
          </div>
          <div class="field">
            <label for="wz-url">Adres strony (WWW)</label>
            <input id="wz-url" v-model="siteUrl" type="url" placeholder="https://twoja-strona.pl">
            <p class="hint">
              Dzięki niemu po naprawie sprawdzimy, czy strona odpowiada poprawnie, a zniszczone pliki
              odzyskamy z archiwum internetu. Opcjonalne, ale bardzo pomaga.
            </p>
          </div>
        </template>

        <!-- Krok 3: zakres -->
        <template v-if="step === 3">
          <div class="cards">
            <button type="button" class="pick tall" :class="{ on: mode === 'report' }" @click="mode = 'report'">
              <span class="ic"><Icon name="search" :size="24" /></span>
              <span class="grow">
                <span class="t">Tylko sprawdź</span>
                <span class="d">Pełny skan i raport zagrożeń. Nie zmieniamy ani jednego pliku. Bezpieczny pierwszy krok.</span>
              </span>
              <span v-if="mode === 'report'" class="chk"><Icon name="check" :size="18" /></span>
            </button>
            <button type="button" class="pick tall" :class="{ on: mode === 'quarantine' }" @click="mode = 'quarantine'">
              <span class="ic"><Icon name="shield-check" :size="24" /></span>
              <span class="grow">
                <span class="t">Sprawdź i napraw <span class="pill ok">zalecane</span></span>
                <span class="d">Złośliwe pliki trafiają do kwarantanny, zainfekowane są naprawiane oryginałami. Każda zmiana jest odwracalna jednym kliknięciem.</span>
              </span>
              <span v-if="mode === 'quarantine'" class="chk"><Icon name="check" :size="18" /></span>
            </button>
          </div>

          <p v-if="mode === 'quarantine' && props.hasBilling === false" class="warnbox">
            <Icon name="receipt" /> Naprawa jest płatna, a Twoje konto nie ma jeszcze danych do faktury.
            <a href="#/rozliczenia" @click="emit('close')">Uzupełnij je w Rozliczeniach</a> albo wybierz „Tylko sprawdź”.
          </p>

          <div class="summary">
            <div><span class="muted">Serwer</span><b>{{ server?.label }}</b></div>
            <div><span class="muted">Katalog</span><b class="mono">{{ target }}</b></div>
            <div><span class="muted">Strona</span><b>{{ siteUrl || "—" }}</b></div>
          </div>

          <details class="adv">
            <summary>Ustawienia zaawansowane</summary>
            <div class="advBody">
              <template v-for="o in advancedOpts" :key="o.key">
                <div v-if="o.kind === 'boolean' || o.kind === 'negate'" class="field check">
                  <input :id="'wz-' + o.key" v-model="advanced[o.key]" type="checkbox">
                  <label :for="'wz-' + o.key">{{ o.label }} — {{ o.description }}</label>
                </div>
                <div v-else class="field">
                  <label :for="'wz-' + o.key">{{ o.label }}</label>
                  <select v-if="o.kind === 'enum'" :id="'wz-' + o.key" v-model="advanced[o.key]">
                    <option v-for="c in o.choices || []" :key="c" :value="c">{{ c }}</option>
                  </select>
                  <!-- step="any": domyślny krok 1 łamie walidację min-confidence=0.75. -->
                  <input
                    v-else :id="'wz-' + o.key" v-model="advanced[o.key]"
                    :type="o.kind === 'number' ? 'number' : 'text'" :step="o.kind === 'number' ? 'any' : undefined"
                    :placeholder="o.kind === 'list' ? 'oddziel spacją' : ''"
                  >
                  <p class="hint">{{ o.description }}</p>
                </div>
              </template>
            </div>
          </details>
        </template>

        <p v-if="err" class="err">{{ err }}</p>
      </div>

      <div class="foot">
        <button v-if="step > 1" type="button" class="sec" @click="step -= 1; err = ''"><Icon name="arrow-left" /> Wstecz</button>
        <span class="spacer" />
        <button v-if="step < 3" type="button" :disabled="step === 1 && !servers.length" @click="next">Dalej <Icon name="arrow-right" /></button>
        <button v-else type="button" :disabled="busy || (mode === 'quarantine' && props.hasBilling === false)" @click="start">
          <Icon :name="mode === 'quarantine' ? 'shield-check' : 'search'" /> {{ busy ? "Uruchamiam…" : mode === "quarantine" ? "Sprawdź i napraw" : "Sprawdź stronę" }}
        </button>
      </div>
    </div>

    <DirPicker
      v-if="picking"
      :server-id="serverId"
      :start="target || '/'"
      @pick="(p) => { pickTarget(p); picking = false; }"
      @close="picking = false"
    />
  </div>
</template>

<style scoped>
.backdrop {
  position: fixed; inset: 0; background: rgba(5,8,14,.62); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center; padding: var(--s4); z-index: 40;
}
.box {
  background: var(--panel); border: 1px solid var(--bd2); border-radius: var(--r3);
  width: min(640px, 100%); max-height: 90vh; display: flex; flex-direction: column;
  box-shadow: 0 24px 64px -12px rgba(0,0,0,.6); overflow: hidden;
}
.head { display: flex; justify-content: space-between; align-items: flex-start; padding: var(--s4) var(--s5) 0; }
.head h2 { font-size: 18px; font-weight: 700; letter-spacing: -.01em; }
.head p { font-size: 12px; margin: 4px 0 0; }
.steps { display: flex; gap: var(--s4); list-style: none; margin: var(--s4) var(--s5) 0; padding: 0 0 var(--s3); border-bottom: 1px solid var(--bd); font-size: 12px; color: var(--mut2); }
.steps li { display: flex; align-items: center; gap: 6px; }
.steps span { width: 20px; height: 20px; border-radius: 50%; display: inline-grid; place-items: center; font-size: 11px; font-weight: 700; background: var(--panel3); color: var(--mut); }
.steps li.on { color: var(--fg); font-weight: 600; }
.steps li.on span { background: var(--acc); color: #fff; }
.steps li.done span { background: var(--ok-dim); color: var(--ok); }
.content { padding: var(--s4) var(--s5); overflow-y: auto; flex: 1; }
.cards { display: flex; flex-direction: column; gap: var(--s2); }
.pick {
  display: flex; align-items: center; gap: var(--s3); text-align: left; width: 100%;
  background: var(--panel2); color: var(--fg); border: 1px solid var(--bd); border-radius: var(--r2);
  padding: var(--s3) var(--s4); font-weight: 400; white-space: normal;
}
.pick:hover:not(:disabled) { border-color: var(--acc); filter: none; background: var(--panel3); }
.pick.on { border-color: var(--acc); background: var(--acc-dim); }
.pick .ic { color: var(--acc); display: grid; place-items: center; }
.pick .t { display: block; font-weight: 650; font-size: 14px; }
.pick .d { display: block; font-size: 12px; color: var(--mut); margin-top: 2px; line-height: 1.5; }
.pick .chk { color: var(--acc); font-weight: 800; }
.chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: var(--s2); }
.chip { background: var(--panel3); color: var(--fg); border: 1px solid var(--bd2); border-radius: 99px; padding: 4px 10px; font-size: 12px; font-weight: 500; font-family: var(--mono); }
.chip.on, .chip:hover { border-color: var(--acc); background: var(--acc-dim); filter: none; }
.summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--s3); margin: var(--s4) 0; font-size: 12px; }
.summary div { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.summary b { word-break: break-all; font-weight: 600; }
@media (max-width: 560px) { .summary { grid-template-columns: 1fr; } }
.warnbox { font-size: 13px; background: var(--warn-dim); border: 1px solid var(--warn); border-radius: var(--r2); padding: var(--s3) var(--s4); margin: var(--s3) 0 0; }
.adv > summary { cursor: pointer; color: var(--mut); font-size: 12px; font-weight: 600; user-select: none; }
.advBody { padding-top: var(--s2); }
.foot { display: flex; gap: var(--s2); padding: var(--s3) var(--s5); border-top: 1px solid var(--bd); background: var(--panel2); }
</style>
